import type { App, ComputedRef } from 'vue';
import { computed, createApp, ref, shallowReactive } from 'vue';
import type { ButtonVariants } from '@/components/base/button/variants';
import DialogRenderer from './Renderer.vue';
import generateId from '@/utils/generateId';
import { i18n } from '@legacy/js/plugin/vue-i18n';

export const Renderer = DialogRenderer;

export interface DialogButton {
    value: string;
    label: string;
    variant?: ButtonVariants['variant'];
    onClick?: (instance: DialogInstance) => void;
}

export interface DialogOptions {
    title: string;
    message: string;
    buttons?: DialogButton[];
    persistent?: boolean;
    noCloseButton?: boolean;
    noBackdropDismiss?: boolean;
    noEscapeDismiss?: boolean;
}

export interface InfoOptions {
    title: string;
    message: string;
}

export interface ConfirmOptions {
    destructive?: boolean;
    title: string;
    message: string;
    confirmButtonLabel?: string;
    cancelButtonLabel?: string;
}

export type DialogDismissReason = 'backdrop' | 'close-button' | 'escape';
export type DialogCloseReason =
    | [reason: 'dismiss', value: DialogDismissReason]
    | [reason: 'button', value: string]
    | [reason: 'programmatic'];

export interface DialogInstance {
    readonly id: string;
    readonly options: DialogOptions;

    readonly promise: Promise<DialogCloseReason>;

    isOpen: ComputedRef<boolean>;
    isClosed: ComputedRef<boolean>;

    close: (reason?: DialogCloseReason) => void;
    /** Called by the rendering component when a button is clicked */
    triggerButton: (value: string) => void;
    /** Called by the rendering component when the dialog is dismissed (backdrop/ESC) */
    triggerDismiss: (reason: DialogDismissReason) => void;
    onClick: (handler: (button: DialogButton, instance: DialogInstance) => void) => DialogInstance;
    onDismiss: (handler: (instance: DialogInstance) => void) => DialogInstance;
}

export interface DialogService {
    readonly dialogs: DialogInstance[];
    info: (options: InfoOptions) => Promise<void>;
    confirm: (options: ConfirmOptions) => Promise<boolean | null>;
}

const dialogs = shallowReactive<DialogInstance[]>([]);

function createInstance(options: DialogOptions): DialogInstance {
    const id = generateId('dialog');

    const clickHandlers: ((button: DialogButton, instance: DialogInstance) => void)[] = [];
    const dismissHandlers: ((instance: DialogInstance) => void)[] = [];

    const closed = ref(false);
    let pendingCloseReason: DialogCloseReason | undefined;

    let resolveClosed!: (reason: DialogCloseReason) => void;
    const closedPromise = new Promise<DialogCloseReason>((resolve) => {
        resolveClosed = resolve;
    });

    const instance: DialogInstance = {
        id,
        options,

        promise: closedPromise,

        isOpen: computed(() => !closed.value),
        isClosed: computed(() => closed.value),

        close(reason?: DialogCloseReason) {
            if (closed.value) return;
            closed.value = true;

            const closeReason = reason ?? pendingCloseReason ?? ['programmatic'];

            const index = dialogs.findIndex(d => d.id === id);
            if (index !== -1) dialogs.splice(index, 1);

            resolveClosed(closeReason);
        },

        triggerButton(value: string) {
            const button = options.buttons?.find(b => b.value === value);
            if (!button) return;
            pendingCloseReason = ['button', value];

            if (button.onClick) {
                button.onClick?.(instance);
            }

            clickHandlers.forEach(h => h(button, instance));
            if (!closed.value) {
                instance.close();
            }
        },

        triggerDismiss(reason: DialogDismissReason) {
            if (closed.value) {
                return
            }

            dismissHandlers.forEach(h => h(instance));
            instance.close(['dismiss', reason]);
        },

        onClick(handler) {
            clickHandlers.push(handler);
            return instance;
        },

        onDismiss(handler) {
            dismissHandlers.push(handler);
            return instance;
        },
    };

    dialogs.push(instance);

    return instance;
}

const dialog: DialogService = {
    dialogs,

    info(options: InfoOptions): Promise<void> {
        const instance = createInstance({
            title: options.title,
            message: options.message,
            buttons: [
                {
                    value: 'close',
                    label: i18n.t('Schließen'),
                    variant: 'primary',
                },
            ],
        });
        return instance.promise.then(() => undefined);
    },

    confirm(options: ConfirmOptions): Promise<boolean | null> {
        const instance = createInstance({
            title: options.title,
            message: options.message,
            noBackdropDismiss: true,
            noEscapeDismiss: true,
            buttons: [
                {
                    value: 'confirm',
                    label: options.confirmButtonLabel || i18n.t('OK'),
                    variant: options.destructive ? 'destructive' : 'primary',
                },
                {
                    value: 'cancel',
                    label: options.cancelButtonLabel || i18n.t('Abbrechen'),
                },
            ],
        });

        return instance.promise.then(([reason, value]) => {
            if (reason === 'dismiss') {
                return null;
            }

            return !!(reason === 'button' && value === 'confirm');
        })
    },
};

let isPluginInstalled = false;

export const dialogPlugin = {
    install(app: App) {
        app.config.globalProperties.$dialog = dialog;

        app.provide('dialog', dialog);

        if (isPluginInstalled) {
            return;
        }
        isPluginInstalled = true;

        // For legacy code that still uses the global variable
        window.legacyDialog = dialog;

        const container = document.createElement('div');
        document.body.appendChild(container);

        const containerApp = createApp(DialogRenderer);
        containerApp.provide('dialog', dialog);
        containerApp.mount(container);
    },
};

export function useDialog(): DialogService {
    return dialog;
}
